<script>
import {
  avatarColourSets,
  stringToPredefinedColourSet,
} from '@/helpers/color-helpers'
import ColorSet from '@/models/general/colorSet'

export default {
  name: 'UserAvatar',
  props: {
    size: {
      type: String,
      default: 'sm',
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      required: true,
    },
    imageUrl: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      default: '',
    },
    fontSize: {
      type: Number,
      default: null,
    },
    colorSet: {
      type: ColorSet,
      default: () => null,
    },
  },
  data() {
    return {
      sizeList: {
        xs: '24',
        sm: '28',
        md: '46',
        lg: '56',
        xl: '62',
        xxl: '74',
      },
      fontSizeList: {
        xs: '10px',
        sm: '12px',
        md: '14px',
        lg: '14px',
        xl: '14px',
        xxl: '16px',
      },
      initials: 'NA',
      color: this.colorSet || avatarColourSets.ateam_purple,
      tooltipEnabled: false,
    }
  },
  computed: {
    compFontSize() {
      if (this.fontSize) return `${this.fontSize}px`

      let fontFromAvatarSize

      try {
        fontFromAvatarSize = this.fontSizeList[this.size]
      } catch {
        fontFromAvatarSize = '12px'
      }

      return fontFromAvatarSize
    },
    compAvatarSize() {
      if (!isNaN(this.size)) return this.size

      return this.sizeList[this.size]
    },
    compInitialTextStyle() {
      return {
        'font-size': this.compFontSize,
        color: this.color?.foreground,
      }
    },
  },
  watch: {
    user: {
      handler() {
        this.initUserObj()
      },
      deep: true,
    },
  },
  mounted() {
    this.initUserObj()
  },
  methods: {
    generateInitials() {
      if (this.name?.length) {
        const allNames = this.name.trim().split(' ')
        const initials = allNames.reduce((acc, curr, index) => {
          if (index === 0 || index === allNames.length - 1) {
            acc = `${acc}${curr.charAt(0).toUpperCase()}`
          }
          return acc
        }, '')
        return initials
      }
      return '?'
    },
    initUserObj() {
      this.initials = this.generateInitials()
      this.color = this.colorSet || stringToPredefinedColourSet(this.name)
    },
  },
}
</script>

<template>
  <section>
    <v-tooltip
      v-model="tooltipEnabled"
      :disabled="!tooltip"
      :data-tooltip-text="tooltipText"
      top
    >
      <template v-slot:activator="{ on }">
        <v-avatar
          v-if="initials"
          data-testid="user-avatar"
          :rounded="rounded"
          :size="compAvatarSize"
          :color="color.background"
          :icon="icon"
          :class="[
            rounded ? 'rounded-lg' : '',
            clickable ? 'cursor-pointer avatar-container' : '',
          ]"
          v-on="on"
          @click="$emit('click')"
        >
          <slot>
            <div if="clickable" class="zoom-icon">
              <v-icon color="white">mdi-magnify-plus-outline</v-icon>
            </div>

            <v-img
              v-if="imageUrl"
              data-testid="user-avatar-img"
              :src="imageUrl"
              :alt="name + ' profile picture'"
            />
            <v-icon
              v-else-if="icon"
              data-testid="user-avatar-icon"
              :style="compInitialTextStyle"
            >
              {{ icon }}
            </v-icon>
            <span
              v-else
              data-testid="user-avatar-initials"
              :class="['font-weight-medium text-uppercase']"
              :style="compInitialTextStyle"
            >
              {{ initials }}
            </span>
          </slot>
        </v-avatar>
      </template>
      <slot name="tooltip">
        <span> {{ tooltipText }} </span>
      </slot>
    </v-tooltip>
  </section>
</template>

<style lang="scss" scoped>
.zoom-icon {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  display: none;
  width: 100%;
  height: 100%;
  padding: auto;
  margin: auto;
  color: #fff;
  text-align: center;
  text-shadow: 0 0 10px #000;
  background-color: rgb(0 0 0 / 30%);
}

.avatar-container:hover {
  .zoom-icon {
    display: block !important;
  }
}
</style>
